import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { Formik } from "formik";
import * as yup from "yup";
import {
    useTheme,
    Box,
    Typography,
    CircularProgress,
    TextField,
    Button,
    IconButton,
    Tooltip,
    FormControlLabel,
    Checkbox,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Avatar,
} from '@mui/material';
import {
    ArrowBackIosNew,
    Delete,
    PhotoCamera,
} from '@mui/icons-material';
import { LAYOUT_HEIGHT, BACKEND_URL } from 'state/constants';
import api from 'state/axios';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const checkoutSchema = yup.object().shape({
    firstName: yup.string().required("required"),
    lastName: yup.string().required("required"),
    email: yup.string().email("invalid email").required("required"),
});

const INITIAL_FORM_VALUES = {
    firstName: '',
    lastName: '',
    location: '',
    occupation: '',
    picture: '',
    email: '',
    role: '',
    departmentId: '',
    isActive: false,
    isChatVisible: true,
};


const User = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const authUser = useSelector((state) => state.user);
    const theme = useTheme();
    const primaryMain = theme.palette.primary.main;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [image, setImage] = useState(null);
    const [picture, setPicture] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);

    const imageInputRef = useRef(null);

    useEffect(() => {
        let mounted = true;

        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);

                const [rolesRes, depsRes, userRes] = await Promise.all([
                    api.get('/roles'),
                    api.get('/departments/active'),
                    userId ? api.get(`/users/${userId}`) : Promise.resolve(null),
                ]);

                if (!mounted) return;

                setRoles(rolesRes.data);
                setDepartments(depsRes.data);

                if (userRes?.data) {
                    const d = userRes.data;
                    setImage(d.picturePath && d.picturePath !== 'undefined'
                        ? `${BACKEND_URL}/assets/${d.picturePath}`
                        : null);
                    setFormValues(prev => ({
                        ...prev,
                        firstName: d.firstName || '',
                        lastName: d.lastName || '',
                        location: d.location || '',
                        occupation: d.occupation || '',
                        email: d.email || '',
                        role: d.role || '',
                        departmentId: d.departmentId || '',
                        isActive: d.isActive ?? false,
                        isChatVisible: d.isChatVisible ?? true,
                    }));
                } else {
                    setImage(null);
                }
            } catch (err) {
                if (mounted) setError(err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        fetchData();
        return () => { mounted = false; };
    }, [userId]);

    useEffect(() => {
        return () => {
            if (image && typeof image === 'string' && image.startsWith('blob:')) {
                URL.revokeObjectURL(image);
            }
        };
    }, [image]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            setImage(URL.createObjectURL(file));
            setPicture(file);
        }
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleImageChange = useCallback((e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(URL.createObjectURL(file));
            setPicture(file);
        }
    }, []);

    const handleRemoveImage = useCallback(() => {
        setImage(null);
        setPicture(null);
        if (imageInputRef.current) imageInputRef.current.value = '';
    }, []);

    const handleFormSubmit = useCallback(async (values, onSubmitProps) => {
        try {
            const formData = new FormData();
            for (const key in values) {
                if (key !== "password" || values.password) {
                    formData.append(key, values[key]);
                }
            }
            if (picture) {
                formData.append("picture", picture);
                formData.append("picturePath", picture.name);
            }

            const url = userId ? `/auth/update/${userId}` : '/auth/register';
            const method = userId ? "put" : "post";

            const response = await api({ method, url, data: formData, withCredentials: true });

            if (response.data) {
                toast.success(userId ? "User updated successfully." : "User created successfully.");
                onSubmitProps.resetForm();
                setImage(null);
                setPicture(null);
                navigate("/users");
            }
        } catch (err) {
            setError(err);
            toast.error(err.response?.data?.message || "Unauthorized");
        }
    }, [picture, userId, navigate]);

    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="User"
                    subtitle={loading ? 'Loading...' : userId ? 'Update' : 'Create'}
                />
                {!loading && (
                    <StyledButton onClick={() => navigate("/users")}>
                        <ArrowBackIosNew />
                    </StyledButton>
                )}
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
                            <form onSubmit={handleSubmit}>
                                {/* Image upload */}
                                <Box display="flex" alignItems="center" gap={2} mb={3}>
                                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                        <Avatar
                                            src={image || undefined}
                                            sx={{
                                                width: 200,
                                                height: 200,
                                                border: `1px solid ${theme.palette.divider}`,
                                                fontSize: '2.5rem',
                                            }}
                                        />
                                        {image && (
                                            <Tooltip title="Clear">
                                                <IconButton
                                                    size="small"
                                                    onClick={handleRemoveImage}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        right: 0,
                                                        p: 1,
                                                        color: '#fff',
                                                        bgcolor: 'error.main',
                                                        '&:hover': { bgcolor: 'error.dark' },
                                                    }}
                                                >
                                                    <Delete sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>

                                    <Box
                                        onClick={() => imageInputRef.current.click()}
                                        onDrop={handleDrop}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        sx={{
                                            flex: 1,
                                            height: 150,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1,
                                            border: `2px dashed ${isDragOver ? primaryMain : theme.palette.divider}`,
                                            borderRadius: 2,
                                            bgcolor: isDragOver ? `${primaryMain}12` : 'transparent',
                                            cursor: 'pointer',
                                            transition: 'border-color 0.2s, background-color 0.2s',
                                            '&:hover': {
                                                borderColor: primaryMain,
                                                bgcolor: `${primaryMain}08`,
                                            },
                                        }}
                                    >
                                        <PhotoCamera sx={{ color: isDragOver ? primaryMain : 'text.secondary', fontSize: 28 }} />
                                        <Typography variant="body2" color={isDragOver ? 'primary' : 'text.secondary'}>
                                            {isDragOver ? 'Drop to upload' : 'Drag & drop or click to upload'}
                                        </Typography>
                                        <Typography variant="caption" color="text.disabled">
                                            PNG, JPG, WEBP
                                        </Typography>
                                    </Box>

                                    <input
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        ref={imageInputRef}
                                        onChange={handleImageChange}
                                        style={{ display: 'none' }}
                                    />
                                </Box>

                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    gap={2}
                                >
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        label="First Name"
                                        name="firstName"
                                        value={values.firstName}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={!!touched.firstName && !!errors.firstName}
                                        helperText={touched.firstName && errors.firstName}
                                        sx={{ gridColumn: { xs: "span 12", lg: "span 6" } }}
                                    />
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        label="Last Name"
                                        name="lastName"
                                        value={values.lastName}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={!!touched.lastName && !!errors.lastName}
                                        helperText={touched.lastName && errors.lastName}
                                        sx={{ gridColumn: { xs: "span 12", lg: "span 6" } }}
                                    />
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        label="Location"
                                        name="location"
                                        value={values.location}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={!!touched.location && !!errors.location}
                                        helperText={touched.location && errors.location}
                                        sx={{ gridColumn: { xs: "span 12", lg: "span 6" } }}
                                    />
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        label="Occupation"
                                        name="occupation"
                                        value={values.occupation}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={!!touched.occupation && !!errors.occupation}
                                        helperText={touched.occupation && errors.occupation}
                                        sx={{ gridColumn: { xs: "span 12", lg: "span 6" } }}
                                    />
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="email"
                                        label="Email"
                                        name="email"
                                        value={values.email}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={!!touched.email && !!errors.email}
                                        helperText={touched.email && errors.email}
                                        sx={{ gridColumn: { xs: "span 12", lg: "span 6" } }}
                                    />
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="password"
                                        label="Password"
                                        name="password"
                                        value={values.password}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={!!touched.password && !!errors.password}
                                        helperText={touched.password && errors.password}
                                        sx={{ gridColumn: { xs: "span 12", lg: "span 6" } }}
                                    />

                                    <FormControl
                                        required
                                        variant="filled"
                                        sx={{ gridColumn: { xs: "span 12", lg: "span 6" } }}
                                    >
                                        <InputLabel>Role</InputLabel>
                                        <Select
                                            variant="filled"
                                            name="role"
                                            value={values.role || ''}
                                            onChange={handleChange}
                                        >
                                            {roles.length > 0 ? (
                                                roles
                                                    .filter(item => authUser.role === 'admin' || item.role !== 'admin')
                                                    .map(item => (
                                                        <MenuItem key={item._id} value={item.role}>
                                                            {item.description}
                                                        </MenuItem>
                                                    ))
                                            ) : (
                                                <MenuItem value="">No roles</MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>

                                    <FormControl
                                        required
                                        variant="filled"
                                        sx={{ gridColumn: { xs: "span 12", lg: "span 6" } }}
                                    >
                                        <InputLabel>Department</InputLabel>
                                        <Select
                                            variant="filled"
                                            name="departmentId"
                                            value={values.departmentId || ''}
                                            onChange={handleChange}
                                        >
                                            <MenuItem value="">--- None ---</MenuItem>
                                            {departments.map(item => (
                                                <MenuItem key={item._id} value={item._id}>
                                                    {item.description || ''}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControlLabel
                                        sx={{ gridColumn: { xs: "span 12", lg: "span 6" } }}
                                        control={
                                            <Checkbox
                                                name="isActive"
                                                color="primary"
                                                checked={values.isActive}
                                                onChange={handleChange}
                                            />
                                        }
                                        label="Is Active"
                                    />
                                    <FormControlLabel
                                        sx={{ gridColumn: { xs: "span 12", lg: "span 6" } }}
                                        control={
                                            <Checkbox
                                                name="isChatVisible"
                                                color="primary"
                                                checked={values.isChatVisible}
                                                onChange={handleChange}
                                            />
                                        }
                                        label="Visible in Chat"
                                    />

                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {userId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default User;
